import { ReactNode } from 'react'

import { IntlConfig } from 'react-intl'

/**
 * react-intl v4+ parses `<tag>` syntax in messages as rich-text elements
 * (react-intl v2's FormattedMessage did NOT — tags were literal text). To keep
 * the many existing messages that contain simple presentational tags rendering
 * without adding a callback at every call site, we register default handlers
 * for the common tags here and pass them to every IntlProvider via
 * `defaultRichTextElements`. A call site can still override any tag by passing
 * its own callback in `values` (e.g. a <span> that needs a specific className).
 */
export const defaultRichTextElements = {
  b: (chunks: ReactNode) => <b>{chunks}</b>,
  strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
  i: (chunks: ReactNode) => <i>{chunks}</i>,
  em: (chunks: ReactNode) => <em>{chunks}</em>,
  u: (chunks: ReactNode) => <u>{chunks}</u>,
  span: (chunks: ReactNode) => <span>{chunks}</span>,
  div: (chunks: ReactNode) => <div>{chunks}</div>,
  p: (chunks: ReactNode) => <p>{chunks}</p>,
  ul: (chunks: ReactNode) => <ul>{chunks}</ul>,
  ol: (chunks: ReactNode) => <ol>{chunks}</ol>,
  li: (chunks: ReactNode) => <li>{chunks}</li>,
  br: () => <br />,
}

/**
 * Many message ids are intentionally absent from the locale file and fall back
 * to the id/defaultMessage. react-intl v2 handled this silently; v7 reports it
 * via onError. Swallow MISSING_TRANSLATION so it does not spam logs or fail
 * tests, while still surfacing genuine formatting errors.
 */
export const onIntlError: NonNullable<IntlConfig['onError']> = (error) => {
  if (error.code === 'MISSING_TRANSLATION') {
    return
  }
  // eslint-disable-next-line no-console
  console.error(error)
}
